import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { overlayVariants, sheetVariants, transition } from '../lib/motion.js';
import { useEventStore } from '../store/useEventStore.js';
import { toast } from '../store/useToast.js';
import { fetchEmbeddedCheckoutForBid } from '../lib/stripeCheckout.js';
import EmbeddedStripeCheckout from './EmbeddedStripeCheckout.jsx';

const QUICK_BOOSTS_CENTS = [
  { label: '0,20 €', cents: 20 },
  { label: '0,50 €', cents: 50 },
  { label: '1 €', cents: 100 },
];

export default function BoostModal({ open, request, onClose }) {
  const boostTrack = useEventStore((s) => s.boostTrack);
  const [customEur, setCustomEur] = useState('');
  const [selectedCents, setSelectedCents] = useState(100);
  const [submitting, setSubmitting] = useState(false);
  const [embeddedSecret, setEmbeddedSecret] = useState(null);
  const submittingRef = useRef(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (open) {
      setSelectedCents(100);
      setCustomEur('');
      submittingRef.current = false;
      setSubmitting(false);
      setEmbeddedSecret(null);
    }
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key !== 'Escape' || !open) return;
      if (embeddedSecret) {
        setEmbeddedSecret(null);
        return;
      }
      if (submitting) return;
      onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, embeddedSecret, submitting]);

  if (!request) return null;

  const title = request.custom_title ?? 'Sans titre';
  const canBoost = request.status === 'pending';
  const customEurValue = parseFloat(String(customEur).replace(',', '.').trim());
  const ctaCents =
    customEur && Number.isFinite(customEurValue)
      ? Math.round(customEurValue * 100)
      : selectedCents;

  async function confirmBoost(amountCents) {
    // Garde anti-double-submit : le bid est créé dès boostTrack ; sans ce verrou
    // un double-tap (ou un clic pendant le fetch checkout) crée un 2e bid en DB.
    if (submittingRef.current) return;
    const cents = Math.max(0, Math.floor(Number(amountCents) || 0));
    if (!canBoost) {
      toast.error('Cette suggestion est déjà acceptée ou close — soutien impossible.');
      onClose?.();
      return;
    }
    if (cents < 20) {
      toast.error('Montant minimum : 0,20 €.');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    const res = await boostTrack(request.id, cents);
    if (!res?.ok) {
      if (res?.reason === 'amount_too_low') {
        toast.error('Montant minimum : 0,20 €.');
      } else if (res?.reason === 'not_boostable' && res?.message) {
        toast.error(res.message);
      } else if (res?.reason === 'rpc_failed') {
        const detail = import.meta.env.DEV && res?.message ? ` (${res.message})` : '';
        toast.error(`Soutien impossible pour le moment. Réessaie.${detail}`);
      } else {
        toast.error('Soutien impossible pour le moment. Réessaie.');
      }
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }

    if (!res.bidId) {
      toast.error('Soutien impossible pour le moment. Réessaie.');
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }
    const returnUrl = window.location.href.split('#')[0];
    const pay = await fetchEmbeddedCheckoutForBid(res.bidId, returnUrl);
    if (!pay.ok) {
      toast.error(pay.message);
      submittingRef.current = false;
      setSubmitting(false);
      return;
    }
    // Checkout Stripe prend le relais : il devient la surface active et bloque le
    // double-submit. On relâche le verrou (fermer le checkout réactive les boutons).
    submittingRef.current = false;
    setSubmitting(false);
    setEmbeddedSecret(pay.clientSecret);
  }

  async function handleCustomBoost() {
    const raw = String(customEur).replace(',', '.').trim();
    const eur = parseFloat(raw);
    if (!Number.isFinite(eur) || eur < 0.2) {
      toast.error('Indique au moins 0,20 €.');
      return;
    }
    const cents = Math.round(eur * 100);
    await confirmBoost(cents);
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/75 backdrop-blur-md p-0 sm:items-center sm:p-4"
          onClick={() => {
            if (!embeddedSecret && !submitting) onClose?.();
          }}
          role="dialog"
          aria-modal="true"
          aria-label="Soutenir cette musique"
          variants={overlayVariants}
          initial={reduceMotion ? false : 'initial'}
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          transition={transition(reduceMotion, 'fast')}
        >
          <motion.div
            className="sheet-surface max-h-[92dvh] w-full max-w-[480px] overflow-y-auto rounded-t-3xl border border-border p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
            variants={sheetVariants}
            initial={reduceMotion ? false : 'initial'}
            animate="animate"
            exit={reduceMotion ? undefined : 'exit'}
            transition={transition(reduceMotion, 'panel')}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-gold">
                  Soutenir cette musique
                </p>
                <p className="mt-1 text-base font-semibold text-white">{title}</p>
                <p className="mt-1 text-xs leading-relaxed text-white/55">
                  Paiement immédiat : même si la piste n'est pas jouée, la cagnotte et ta visibilité
                  augmentent — le DJ reste décideur.
                </p>
              </div>
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  if (embeddedSecret) setEmbeddedSecret(null);
                  else if (!submitting) onClose?.();
                }}
                className="rounded-xl border border-border px-3 py-1.5 text-xs text-white/70 hover:bg-white/5 disabled:opacity-40"
              >
                Fermer
              </button>
            </div>

            {!canBoost ? (
              <p className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-xs leading-relaxed text-amber-100/95">
                Cette suggestion a déjà été acceptée par le DJ — le soutien n’est plus disponible.
              </p>
            ) : null}

            <p className="mt-4 text-[11px] text-white/40">
              Paiement sécurisé par Stripe (carte). Commission plateforme éventuelle selon contrat
              établissement.
            </p>

            <div className="mt-4 grid grid-cols-3 gap-2">
              {QUICK_BOOSTS_CENTS.map((qb) => {
                const sel = selectedCents === qb.cents && !customEur;
                return (
                  <button
                    key={qb.cents}
                    type="button"
                    disabled={!canBoost || submitting}
                    onClick={() => {
                      setCustomEur('');
                      setSelectedCents(qb.cents);
                    }}
                  className={`min-h-[44px] rounded-xl border px-2 py-2.5 font-mono text-sm font-semibold tabular-nums transition ${
                    !canBoost || submitting
                      ? 'cursor-not-allowed border-border bg-white/[0.02] text-white/35'
                      : sel
                        ? 'border-gold bg-gold/20 text-gold'
                        : 'border-border bg-white/[0.04] text-white/85 hover:bg-white/[0.08]'
                  }`}
                  >
                    {qb.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 space-y-2">
              <label className="text-[11px] font-medium uppercase tracking-wider text-white/45">
                Montant libre (min. 0,20 €)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="ex. 1,50"
                  value={customEur}
                  onChange={(e) => setCustomEur(e.target.value)}
                  className="input flex-1"
                  disabled={submitting || !canBoost}
                />
                <button
                  type="button"
                  onClick={() => handleCustomBoost()}
                  disabled={submitting || !canBoost}
                  className="btn-secondary min-h-[44px] shrink-0 whitespace-nowrap px-4 py-2.5 text-xs"
                >
                  OK
                </button>
              </div>
            </div>

            <button
              type="button"
              disabled={submitting || !canBoost}
              onClick={() => (customEur ? handleCustomBoost() : confirmBoost(selectedCents))}
              className="btn-money mt-5 min-h-[44px] w-full py-3 text-sm"
            >
              {submitting
                ? '…'
                : `Payer et soutenir (${(ctaCents / 100).toFixed(2)} €)`}
            </button>

            <p className="mt-3 text-center text-[10px] text-white/38">
              Le pourcentage sur la carte est indicatif uniquement · pas une place garantie dans le mix.
            </p>
          </motion.div>
          </motion.div>
          {embeddedSecret ? (
            <EmbeddedStripeCheckout
              clientSecret={embeddedSecret}
              onClose={() => setEmbeddedSecret(null)}
              onMountError={(msg) => {
                toast.error(msg);
                setEmbeddedSecret(null);
              }}
            />
          ) : null}
        </>
      )}
    </AnimatePresence>
  );
}
